import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Params, Router, RouterStateSnapshot } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { OidcClientService } from '@upmc-healthplan-npm/oidc-client';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IUser } from '../models/state.model';
import { StorageService } from '../services/storage.service';
import { actionLoadUserFromSessionStorage } from '../state/core.actions';
import { getSignedInUser } from '../state/core.selectors';
import { POLAuthService } from '../services/auth.service';

export interface SignOutOptions {
  intendedRoute?: string;
  signOut?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthGuard {
  authService = inject(POLAuthService);
  user$: Observable<IUser> = this.store.pipe(select(getSignedInUser));
  signedInUser: IUser;
  static readonly B2C_POLICY_REGEX = /\/(B2C_1A\w+)/i;

  constructor(private oidc: OidcClientService,
    private router: Router,
    private store: Store,
    private storage: StorageService) {
    this.user$.subscribe(user => this.signedInUser = user);
  }
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authCheck(route.queryParams, state);
  }

  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authCheck(route.queryParams, state);
  }

  authCheck(queryParams: Params, state: RouterStateSnapshot): Observable<boolean> {
    const unsubscribe = new Subject<void>();
    return new Observable((observer) => {
      const user = this.oidc.getUser();

      if (user) {
        const expired = this.oidc.isExpired();

        if (!expired) {
          observer.next(true);
        } else {
          observer.next(false);
          this.authService.signOutRedirect({ intendedRoute: state.url });
        }
      } else {
        this.oidc
          .getUserComputed()
          .pipe(takeUntil(unsubscribe))
          .subscribe({
            next: (userComputed) => {
              if (userComputed) {
                observer.next(true);
                const existingUserProfile = this.storage.getItem('user-profile');
                if (existingUserProfile && !this.signedInUser) {
                  this.store.dispatch(actionLoadUserFromSessionStorage({ user: JSON.parse(existingUserProfile) }))
                }
              } else {
                observer.next(false);
                this.storage.removeItem('user-profile');
                // this.router.navigate(['unauthorized']);
                this.authService.signOutRedirect({ intendedRoute: state.url }, this.attachAuthParams(queryParams));
              }
              unsubscribe.next();
            },
            error: () => this.authService.signOutRedirect({ intendedRoute: state.url }, this.attachAuthParams(queryParams))
          });
      }
    });
  }


  attachAuthParams(queryParams: Params) {
    if (queryParams) {
      const params = { ...queryParams };
      const configParams: any = {};

      if (queryParams.policy) {
        configParams.data = params.policy;
        this.oidc.setPolicy(AuthGuard.B2C_POLICY_REGEX, params.policy);
        delete params.policy;
      }

      if (queryParams.redirect_uri) {
        configParams.redirect_uri = params.redirect_uri;
        delete params.redirect_uri;
      }

      if (queryParams.client_id) {
        this.oidc.setClientId(params.client_id);
        delete params.client_id;
      }

      return { ...configParams, extraQueryParams: params };
    } else {
      return {};
    }
  }
}
